import { useMemo, useState } from 'react';
import { Check, ChevronDown, Filter, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../components/ui/utils';
import { FILTER_LABELS, countBy, sortCountsDesc } from '../hr-dashboard-utils';
import type { HrFilters, HrPerson } from '../../../types/hr-dashboard.types';


interface FilterOption {
  value: string;
  count: number;
}

export function FilterSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const shown = useMemo(
    () =>
      options.filter((option) =>
        option.value.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [options, query]
  );

  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value]
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${label}`}
          className={cn(
            'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-card px-3 text-left text-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selected.length > 0 && 'border-link'
          )}
        >
          <span className="min-w-0">
            <span className="mr-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {label}
            </span>
            <span className="truncate text-foreground">{summary}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b border-border p-2">
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`Search ${label.toLowerCase()}…`}
            className="h-8"
            aria-label={`Search ${label}`}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {shown.length === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">No matches</p>
          ) : (
            shown.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span
                    className={cn(
                      'grid h-4 w-4 shrink-0 place-items-center rounded border',
                      checked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card'
                    )}
                  >
                    {checked ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-foreground">{option.value}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">{option.count}</span>
                </button>
              );
            })
          )}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border p-2">
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange([...new Set([...selected, ...shown.map((o) => o.value)])])}
            >
              Select shown
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
              Clear
            </Button>
          </div>
          <Button type="button" size="sm" onClick={() => setOpen(false)}>
            Done
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function HrFilterBar({
  people,
  filters,
  onChange,
  onClear,
  resultCount,
}: {
  people: HrPerson[];
  filters: HrFilters;
  onChange: (key: keyof HrFilters, values: string[]) => void;
  onClear: () => void;
  resultCount: number;
}) {
  const options = useMemo(() => {
    const build = (getKey: (person: HrPerson) => string | null | undefined) =>
      sortCountsDesc(countBy(people, getKey)).map(([value, count]) => ({ value, count }));

    return {
      dept: build((person) => person.dept),
      region: build((person) => person.region),
      emp: build((person) => person.emp || 'Not recorded'),
      level: build((person) => person.level),
    };
  }, [people]);

  const active = (Object.keys(FILTER_LABELS) as (keyof HrFilters)[]).flatMap((key) =>
    filters[key].map((value) => ({ key, value }))
  );

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {(Object.keys(FILTER_LABELS) as (keyof HrFilters)[]).map((key) => (
          <FilterSelect
            key={key}
            label={FILTER_LABELS[key]}
            options={options[key]}
            selected={filters[key]}
            onChange={(values) => onChange(key, values)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
          Showing {resultCount} of {people.length} active staff
        </span>

        {active.map(({ key, value }) => (
          <span
            key={`${key}:${value}`}
            className="inline-flex items-center gap-1 rounded-full bg-selection px-2.5 py-0.5 text-xs font-medium text-link"
          >
            {value}
            <button
              type="button"
              onClick={() => onChange(key, filters[key].filter((entry) => entry !== value))}
              className="rounded-full p-0.5 hover:bg-accent"
              aria-label={`Remove ${value} filter`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}

        {active.length > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : null}
      </div>
    </div>
  );
}
